/**
 * InputRow — auto-resizing textarea + send/stop button + voice dictation
 * (Plan 05-07b Task 2; mic wired to Web Speech API in Wave 2).
 *
 * #158 (steg 8a, Stell_Assistent): feltet er `field` med radius 28 og 56 px
 * høyde, mikrofonen en rund sekundærknapp og send den lime knappen (56 px).
 * Raden har ingen egen bakgrunn lenger: før var den et flatt `bg-bg`-bånd
 * over gløden (#151). Den står nederst i AssistantRoute, under tråden.
 *
 * Auto-resizes from 1 to 5 rows. Enter submits, Shift+Enter newlines.
 * Send button disabled when empty or streaming. Send replaced by Stop during streaming.
 *
 * Mic: when the Web Speech API is available (desktop Chrome / Safari, the
 * kitchen tablet) the button toggles `nb-NO` dictation that streams recognised
 * words into the textarea; the user reviews and sends via the normal flow (no
 * auto-send). Where it is unsupported (iOS Safari / installed iOS PWA) the
 * button shows a short "ikke støttet" hint instead.
 *
 * Per UI-SPEC §Input Row:
 * - Input max 4096 chars (T-05-07b-INPUT-TOOLONG mitigation)
 * - Touch targets ≥ 44×44px
 *
 * data-testid: assistant-input, assistant-send-button, assistant-stop-button, assistant-mic-button
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { useSpeechRecognition } from './useSpeechRecognition';

interface InputRowProps {
  onSubmit: (text: string) => void;
  onStop: () => void;
  isStreaming: boolean;
}

export function InputRow({ onSubmit, onStop, isStreaming }: InputRowProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [micHint, setMicHint] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Text already in the box when dictation starts; recognised words are
  // appended to this base so interim updates replace cleanly each result.
  const dictationBaseRef = useRef('');

  function flashHint(msg: string) {
    setMicHint(msg);
    setTimeout(() => setMicHint((cur) => (cur === msg ? null : cur)), 2500);
  }

  const speech = useSpeechRecognition({
    onResult: (transcript, isFinal) => {
      const base = dictationBaseRef.current;
      const next = (base ? `${base} ${transcript}` : transcript).slice(0, 4096);
      setText(next);
      if (isFinal) dictationBaseRef.current = next; // settle, so a follow-up utterance appends
    },
    onError: (code) => {
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        flashHint(t('assistant.input.mic_denied'));
      } else if (code !== 'no-speech' && code !== 'aborted') {
        flashHint(t('assistant.input.mic_error'));
      }
    },
  });

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function submit() {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    if (speech.listening) speech.stop();
    onSubmit(trimmed);
    setText('');
    dictationBaseRef.current = '';
  }

  function micClick() {
    if (isStreaming) return;
    if (!speech.supported) {
      flashHint(t('assistant.input.mic_unsupported'));
      return;
    }
    if (speech.listening) {
      speech.stop();
      return;
    }
    setMicHint(null);
    dictationBaseRef.current = text.trim();
    speech.start();
  }

  // Auto-resize: count newlines, clamp 1..5
  const rows = Math.min(5, Math.max(1, text.split('\n').length));

  return (
    <div>
      <div className="flex items-end gap-2">
        <textarea
          ref={taRef}
          data-testid="assistant-input"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 4096))}
          onKeyDown={handleKey}
          rows={rows}
          placeholder={t('assistant.input.placeholder')}
          className="field max-h-36 min-h-14 flex-1 resize-none rounded-xl px-4.5 focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={t('assistant.input.label')}
        />

        <button
          data-testid="assistant-mic-button"
          aria-label={t('assistant.input.mic_aria')}
          aria-pressed={speech.listening}
          onClick={micClick}
          className={
            speech.listening
              ? 'flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-on-primary motion-safe:animate-pulse'
              : 'flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-secondary-line bg-secondary text-ink hover:bg-inset'
          }
          type="button"
        >
          <Icon.Mic size={22} />
        </button>

        {isStreaming ? (
          <button
            data-testid="assistant-stop-button"
            aria-label={t('assistant.input.stop_aria')}
            onClick={onStop}
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-solid text-on-solid hover:opacity-90"
            type="button"
          >
            <Icon.Stop size={22} />
          </button>
        ) : (
          <button
            data-testid="assistant-send-button"
            aria-label={t('assistant.input.send_aria')}
            disabled={!text.trim()}
            onClick={submit}
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-fab text-on-fab hover:opacity-90 disabled:pointer-events-none disabled:opacity-40"
            type="button"
          >
            <Icon.Send size={22} />
          </button>
        )}
      </div>

      {speech.listening && (
        <div className="mt-2 px-1 text-xs font-bold text-ink-2" data-testid="assistant-mic-listening">
          {t('assistant.input.mic_listening')}
        </div>
      )}

      {micHint && (
        <div className="mt-2 px-1 text-xs text-ink-2" data-testid="assistant-mic-hint">
          {micHint}
        </div>
      )}
    </div>
  );
}
